/**
 * LookbookPanel — "Cast in Costume on Location" cinematic stills.
 *
 * Renders below the Locations grid in the Asset Bible. Auto-populates after
 * the bible finishes generating (backend kicks off `/lookbook/generate-all`
 * automatically) and polls until done. Users can:
 *   - View 2:3 cinematic stills per cast↔costume↔location triple
 *   - Manually add a new triple via the pair-picker at the top
 *   - Regenerate a single entry, mark canonical (star), or delete
 *   - Click to open a lightbox
 *
 * NOTE: Lookbook output is currently a pitch / approval artifact. It is NOT
 * yet wired into keyframe generation as a reference (that's a separate
 * downstream change — composition still must come from the shot description,
 * not the lookbook composite).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Users, Shirt, MapPin, Loader2, Plus, RefreshCw, Star, Trash2, Maximize2, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;

const LookbookCard = ({ entry, onRegenerate, onToggleSelected, onDelete, onOpen }) => {
  const isProcessing = entry.status === 'processing';
  const isFailed = entry.status === 'failed';
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-white/10 bg-white/[0.02] group"
      style={{ aspectRatio: '16 / 9' }}
      data-testid={`lookbook-card-${entry.id}`}
    >
      {isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#FF9500] z-10 bg-black/30">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-[10px] uppercase tracking-wider">Rendering…</span>
        </div>
      )}
      {isFailed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-red-400 z-10 bg-black/40 p-3 text-center">
          <AlertCircle className="w-6 h-6" />
          <span className="text-[10px]">{entry.error?.slice(0, 120) || 'Generation failed'}</span>
          <button
            onClick={() => onRegenerate(entry.id)}
            className="mt-1 px-2 py-1 rounded-md bg-white/10 text-[10px] hover:bg-white/20"
            data-testid={`lookbook-retry-${entry.id}`}
          >
            Retry
          </button>
        </div>
      )}
      {entry.url && !isProcessing && !isFailed && (
        <img
          src={entry.url}
          alt={`${entry.cast_name} in ${entry.costume_name} at ${entry.location_name}`}
          className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
          onClick={() => onOpen(entry)}
          loading="lazy"
        />
      )}

      {/* Top — chips */}
      <div className="absolute top-0 left-0 right-0 p-1.5 flex flex-wrap gap-1 bg-gradient-to-b from-black/70 via-black/30 to-transparent pointer-events-none">
        <span className="px-1.5 py-0.5 rounded text-[9px] bg-ai-accent/20 text-ai-accent border border-ai-accent/30 flex items-center gap-1">
          <Users className="w-2.5 h-2.5" /> {entry.cast_name?.slice(0, 14)}
        </span>
        <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#FF2D55]/20 text-[#FF2D55] border border-[#FF2D55]/30 flex items-center gap-1">
          <Shirt className="w-2.5 h-2.5" /> {entry.costume_name?.slice(0, 14)}
        </span>
        <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#FF9500]/20 text-[#FF9500] border border-[#FF9500]/30 flex items-center gap-1">
          <MapPin className="w-2.5 h-2.5" /> {entry.location_name?.slice(0, 14)}
        </span>
      </div>

      {/* Bottom — actions on hover */}
      {entry.url && !isProcessing && (
        <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-between items-center bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSelected(entry.id); }}
              className={`p-1.5 rounded-md ${entry.selected ? 'bg-yellow-500/30 text-yellow-300' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
              title={entry.selected ? 'Unmark canonical' : 'Mark canonical'}
              data-testid={`lookbook-star-${entry.id}`}
            >
              <Star className="w-3 h-3" fill={entry.selected ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerate(entry.id); }}
              className="p-1.5 rounded-md bg-white/10 text-white/60 hover:bg-white/20"
              title="Regenerate"
              data-testid={`lookbook-regen-${entry.id}`}
            >
              <RefreshCw className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(entry); }}
              className="p-1.5 rounded-md bg-white/10 text-white/60 hover:bg-white/20"
              title="Open"
              data-testid={`lookbook-open-${entry.id}`}
            >
              <Maximize2 className="w-3 h-3" />
            </button>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this lookbook entry?')) onDelete(entry.id); }}
            className="p-1.5 rounded-md bg-white/10 text-red-300/80 hover:bg-red-500/20 hover:text-red-300"
            title="Delete"
            data-testid={`lookbook-delete-${entry.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

const PairPicker = ({ assetBible, onGenerate, busy }) => {
  const [castId, setCastId] = useState('');
  const [costumeId, setCostumeId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [scene, setScene] = useState('');

  const cast = assetBible?.cast_candidates || [];
  const costumes = assetBible?.costume_candidates || [];
  const locations = assetBible?.corner_candidates || [];

  const canSubmit = castId && costumeId && locationId && !busy;

  return (
    <div className="p-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] mb-3">
      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Add a custom lookbook entry</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <select
          value={castId}
          onChange={(e) => setCastId(e.target.value)}
          className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white/80"
          data-testid="lookbook-pick-cast"
        >
          <option value="">Pick cast…</option>
          {cast.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={costumeId}
          onChange={(e) => setCostumeId(e.target.value)}
          className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white/80"
          data-testid="lookbook-pick-costume"
        >
          <option value="">Pick costume…</option>
          {costumes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white/80"
          data-testid="lookbook-pick-location"
        >
          <option value="">Pick location…</option>
          {locations.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={scene}
          onChange={(e) => setScene(e.target.value)}
          placeholder="Optional scene cue — e.g. 'morning, leaving for office'"
          className="flex-1 px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white/80 placeholder:text-white/30"
          data-testid="lookbook-scene-input"
        />
        <button
          onClick={() => { onGenerate({ cast_id: castId, costume_id: costumeId, location_id: locationId, scene_descriptor: scene }); setScene(''); }}
          disabled={!canSubmit}
          className="px-3 py-1.5 rounded-md bg-ai-accent/20 text-ai-accent border border-ai-accent/40 text-xs font-medium hover:bg-ai-accent/30 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
          data-testid="lookbook-add-btn"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
};

const Lightbox = ({ entry, onClose }) => {
  if (!entry) return null;
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="lookbook-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
      >
        <X className="w-5 h-5 text-white" />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="max-h-full max-w-3xl">
        <img src={entry.url} alt="Lookbook" className="max-h-[85vh] w-auto rounded-xl shadow-2xl" />
        <div className="mt-3 text-xs text-white/60 flex flex-wrap gap-3 justify-center">
          <span><Users className="w-3 h-3 inline mr-1" />{entry.cast_name}</span>
          <span><Shirt className="w-3 h-3 inline mr-1" />{entry.costume_name}</span>
          <span><MapPin className="w-3 h-3 inline mr-1" />{entry.location_name}</span>
          {entry.scene_descriptor && <span className="italic">"{entry.scene_descriptor}"</span>}
        </div>
      </div>
    </div>
  );
};

export const LookbookPanel = ({ projectId, assetBible, onChange }) => {
  const [data, setData] = useState({ candidates: [], status: null, total: 0, completed: 0, failed: 0, error: null });
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/lookbook`);
      setData(res.data || { candidates: [], status: null });
      return res.data;
    } catch (e) {
      // 404 is fine — asset bible just hasn't been built yet
      if (e?.response?.status !== 404) {
        // eslint-disable-next-line no-console
        console.warn('[Lookbook] fetch failed', e);
      }
      return null;
    }
  }, [projectId]);

  // Initial fetch + poll while any entry is "processing"
  useEffect(() => {
    if (!projectId) return undefined;
    fetchStatus();
    const tick = async () => {
      const d = await fetchStatus();
      const stillProcessing = d?.status === 'processing' || (d?.candidates || []).some((c) => c.status === 'processing');
      if (stillProcessing) {
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [projectId, fetchStatus]);

  const generateAll = async () => {
    if (!window.confirm('Generate (or regenerate) all lookbook entries from your script\'s cast/costume/location triples? This consumes GPT Image 2 credits (~$0.20 each).')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/lookbook/generate-all`);
      await fetchStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to start lookbook generation');
    } finally {
      setBusy(false);
    }
  };

  const generateOne = async (body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/lookbook/generate`, body);
      await fetchStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to start');
    } finally {
      setBusy(false);
    }
  };

  const regenerateOne = async (entryId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/lookbook/${entryId}/regenerate`);
      await fetchStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to regenerate');
    }
  };

  const toggleSelected = async (entryId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/lookbook/${entryId}/select`);
      await fetchStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    }
  };

  const deleteOne = async (entryId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/lookbook/${entryId}`);
      await fetchStatus();
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  // Hide the panel until the asset bible has at least one of each kind
  const canRender = !!(
    (assetBible?.cast_candidates?.length || 0) > 0
    && (assetBible?.costume_candidates?.length || 0) > 0
    && (assetBible?.corner_candidates?.length || 0) > 0
  );
  if (!canRender) return null;

  const progress = data.total > 0
    ? Math.round(((data.completed + data.failed) / data.total) * 100)
    : 0;

  return (
    <div className="mt-4 p-4 rounded-2xl border border-white/10 bg-white/[0.015]" data-testid="lookbook-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <span className="text-lg">📔</span>
            Lookbook <span className="text-white/40 font-normal text-xs">— cast in costume on location</span>
          </h3>
          <p className="text-[10px] text-white/40 mt-0.5">
            16:9 cinematic composites via GPT Image 2 with all 3 references — environment preserved, cast grounded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.status === 'processing' && (
            <span className="text-[10px] text-[#FF9500] flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              {data.completed + data.failed}/{data.total} ({progress}%)
            </span>
          )}
          <button
            onClick={generateAll}
            disabled={busy || data.status === 'processing'}
            className="px-3 py-1.5 rounded-md text-xs bg-ai-accent/15 text-ai-accent border border-ai-accent/30 hover:bg-ai-accent/25 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="lookbook-generate-all-btn"
          >
            {data.status === 'processing' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {data.candidates.length > 0 ? 'Regenerate all' : 'Generate all'}
          </button>
        </div>
      </div>

      {data.error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-[11px]">
          {data.error}
        </div>
      )}

      <PairPicker assetBible={assetBible} onGenerate={generateOne} busy={busy} />

      {data.candidates.length === 0 ? (
        <div className="p-6 rounded-xl border border-dashed border-white/10 bg-white/[0.02] text-center">
          <p className="text-xs text-white/40">
            {data.status === 'processing'
              ? 'Rendering lookbooks in the background…'
              : 'No lookbooks yet. Click "Generate all" to auto-render from your script triples, or add a custom one above.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.candidates.map((entry) => (
            <LookbookCard
              key={entry.id}
              entry={entry}
              onRegenerate={regenerateOne}
              onToggleSelected={toggleSelected}
              onDelete={deleteOne}
              onOpen={setLightbox}
            />
          ))}
        </div>
      )}

      <Lightbox entry={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};

export default LookbookPanel;
